<script lang="ts">
	import type { WithElementRef } from 'bits-ui'
	import { cn } from 'tailwind-variants'

	import {
		type CarouselAPI,
		type CarouselProps,
		type EmblaContext,
		setEmblaContext,
	} from './context'

	let {
		ref = $bindable(null),
		opts = {},
		plugins = [],
		setApi = () => {},
		orientation = 'horizontal',
		class: className,
		children,
		...restProps
	}: WithElementRef<CarouselProps> = $props()

	// svelte-ignore state_referenced_locally
	const carouselState = $state<EmblaContext>({
		api: undefined,
		scrollPrev,
		scrollNext,
		orientation,
		canScrollNext: false,
		canScrollPrev: false,
		handleKeyDown,
		options: opts,
		plugins,
		onInit,
		scrollSnaps: [],
		selectedIndex: 0,
		scrollTo,
	})

	setEmblaContext(carouselState)

	function scrollPrev() {
		carouselState.api?.scrollPrev()
	}

	function scrollNext() {
		carouselState.api?.scrollNext()
	}

	function scrollTo(index: number, jump?: boolean) {
		carouselState.api?.scrollTo(index, jump)
	}

	function onSelect() {
		if (!carouselState.api) return
		carouselState.selectedIndex = carouselState.api.selectedScrollSnap()
		carouselState.canScrollNext = carouselState.api.canScrollNext()
		carouselState.canScrollPrev = carouselState.api.canScrollPrev()
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === 'ArrowLeft') {
			e.preventDefault()
			scrollPrev()
		} else if (e.key === 'ArrowRight') {
			e.preventDefault()
			scrollNext()
		}
	}

	function onInit(event: CustomEvent<CarouselAPI>) {
		carouselState.api = event.detail
		setApi(carouselState.api)

		carouselState.scrollSnaps = carouselState.api.scrollSnapList()
		carouselState.api.on('select', onSelect)
		onSelect()
	}

	$effect(() => {
		return () => {
			carouselState.api?.off('select', onSelect)
		}
	})
</script>

<div
	bind:this={ref}
	class={cn('relative', className)}
	aria-roledescription="carousel"
	data-slot="carousel"
	role="region"
	{...restProps}
>
	{@render children?.()}
</div>
